// const API_ROOT = "http://localhost:8000";
const API_ROOT = "https://omr-nyb6.onrender.com";

const MAX_RETRIES_PER_FILE = 1;


function getErrorMessage(data, fallback) {
  if (
    data
    && typeof data.detail === "string"
  ) {
    return data.detail;
  }

  if (
    data
    && typeof data.error === "string"
  ) {
    return data.error;
  }

  return fallback;
}


async function readResponseData(response) {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return {
      detail: text,
    };
  }
}


async function uploadSingleFile({
  endpoint,
  file,
  answerKey,
  threshold,
  forceOneAnswer,
}) {
  const formData = new FormData();

  // The backend receives exactly one file in this request.
  formData.append(
    "images",
    file,
    file.name
  );

  formData.append(
    "answer_key",
    answerKey
  );

  formData.append(
    "force_one_answer",
    String(forceOneAnswer)
  );

  if (threshold.trim() !== "") {
    formData.append(
      "threshold",
      threshold
    );
  }

  const response = await fetch(
    `${API_ROOT}${endpoint}`,
    {
      method: "POST",
      body: formData,
    }
  );

  const data = await readResponseData(
    response
  );

  if (!response.ok) {
    throw new Error(
      getErrorMessage(
        data,
        `OMR processing failed with HTTP ${response.status}.`
      )
    );
  }

  const sheet = data.results?.[0];

  if (!sheet) {
    throw new Error(
      "The backend did not return a result for this file."
    );
  }

  return sheet;
}


async function uploadSingleFileWithRetry(
  requestValues
) {
  let lastError = null;

  for (
    let attempt = 0;
    attempt <= MAX_RETRIES_PER_FILE;
    attempt += 1
  ) {
    try {
      return await uploadSingleFile(
        requestValues
      );
    } catch (error) {
      lastError = error;

      if (
        attempt
        < MAX_RETRIES_PER_FILE
      ) {
        // Small pause before retrying a temporary
        // network or hosting failure.
        await new Promise(
          (resolve) =>
            setTimeout(resolve, 1000)
        );
      }
    }
  }

  throw lastError;
}


function buildCombinedResponse({
  results,
  questionCount,
}) {
  const successful = results.filter(
    (sheet) => sheet.success
  );

  const totalCorrect = successful.reduce(
    (total, sheet) =>
      total
      + Number(
        sheet.summary?.correct || 0
      ),
    0
  );

  const totalPercentage = successful.reduce(
    (total, sheet) =>
      total
      + Number(
        sheet.summary?.percentage || 0
      ),
    0
  );

  return {
    success: successful.length > 0,
    examQuestionCount: questionCount,

    batchSummary: {
      uploaded: results.length,
      processed: successful.length,
      failed:
        results.length
        - successful.length,
      totalCorrect,
      averagePercentage:
        successful.length > 0
          ? Number(
              (
                totalPercentage
                / successful.length
              ).toFixed(2)
            )
          : 0,
    },

    results,
  };
}


async function uploadBatchSequentially({
  endpoint,
  questionCount,
  files,
  answerKey,
  threshold,
  forceOneAnswer,
  onProgress,
}) {
  const results = [];

  for (
    let index = 0;
    index < files.length;
    index += 1
  ) {
    const file = files[index];

    try {
      const backendResult =
        await uploadSingleFileWithRetry({
          endpoint,
          file,
          answerKey,
          threshold,
          forceOneAnswer,
        });

      results.push({
        ...backendResult,

        // Give every combined result a unique ID.
        id: index + 1,

        // Keep the browser filename if the backend
        // does not provide one.
        filename:
          backendResult.filename
          || file.name,
      });
    } catch (error) {
      // One failed file does not stop the remaining
      // files from being processed.
      results.push({
        id: index + 1,
        filename: file.name,
        success: false,
        error:
          error instanceof Error
            ? error.message
            : String(error),
      });
    }

    if (
      typeof onProgress === "function"
    ) {
      onProgress({
        completed: index + 1,
        total: files.length,
        filename: file.name,
        lastResult:
          results[
            results.length - 1
          ],
      });
    }
  }

  // This is returned only after every file has
  // completed, so the page displays all results
  // together.
  return buildCombinedResponse({
    results,
    questionCount,
  });
}


// Separate frontend API function for 50 questions.
export function check50QuestionSheets(
  formValues
) {
  return uploadBatchSequentially({
    endpoint:
      "/api/exam-50/check-batch",
    questionCount: 50,
    ...formValues,
  });
}


// Separate frontend API function for 100 questions.
export function check100QuestionSheets(
  formValues
) {
  return uploadBatchSequentially({
    endpoint:
      "/api/exam-100/check-batch",
    questionCount: 100,
    ...formValues,
  });
}
